import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useAuth } from '../../auth/useAuth.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const STATUS_VARIANT = {
  DRAFT: 'neutral', SUBMITTED: 'warning', APPROVED: 'success', REJECTED: 'danger', CONVERTED: 'info',
};

export function RequisitionDetailModal({ reqId, onClose }) {
  const { t } = useTranslation();
  const { roles } = useAuth();
  const isAdmin = roles.includes('admin');
  const queryClient = useQueryClient();
  const [supplierId, setSupplierId] = useState('');
  const [error, setError] = useState(null);

  const detail = useQuery({
    queryKey: ['inventory', 'requisition', reqId],
    queryFn: () => inventoryApi.getRequisition(reqId).then((r) => r.items),
    enabled: Boolean(reqId),
  });
  const suppliers = useQuery({ queryKey: ['inventory', 'suppliers'], queryFn: () => inventoryApi.listSuppliers().then((r) => r.items), enabled: Boolean(reqId) });

  const req = detail.data;
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['inventory', 'requisitions'] });
    queryClient.invalidateQueries({ queryKey: ['inventory', 'requisition', reqId] });
  };
  const run = (fn) => ({ onSuccess: () => { setError(null); invalidate(); }, onError: (e) => setError(e?.message || t('common.loadError')), mutationFn: fn });

  const submit = useMutation(run(() => inventoryApi.submitRequisition(reqId)));
  const approve = useMutation(run(() => inventoryApi.approveRequisition(reqId)));
  const reject = useMutation(run(() => inventoryApi.rejectRequisition(reqId)));
  const convert = useMutation({ ...run(() => inventoryApi.convertRequisition(reqId, { supplierId })), onSuccess: () => { invalidate(); onClose(); } });
  const busy = submit.isPending || approve.isPending || reject.isPending || convert.isPending;

  return (
    <Modal
      open={Boolean(reqId)}
      onClose={onClose}
      title={req ? `${t('req.detail.title')} ${req.requisitionNumber}` : t('req.detail.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>
          {req?.status === 'DRAFT' && (
            <Button variant="primary" disabled={busy} onClick={() => submit.mutate()}>{t('req.action.submit')}</Button>
          )}
          {req?.status === 'SUBMITTED' && isAdmin && (
            <>
              <Button variant="ghost" disabled={busy} onClick={() => reject.mutate()}>{t('req.action.reject')}</Button>
              <Button variant="primary" disabled={busy} onClick={() => approve.mutate()}>{t('req.action.approve')}</Button>
            </>
          )}
          {req?.status === 'APPROVED' && (
            <Button variant="primary" disabled={busy || !supplierId} onClick={() => convert.mutate()}>{t('req.action.convert')}</Button>
          )}
        </>
      }
    >
      {req && (
        <>
          <p className="muted text-sm">
            <Badge variant={STATUS_VARIANT[req.status]}>{t(`req.status.${req.status}`)}</Badge>
            {req.justification ? ` · ${req.justification}` : ''}
          </p>
          {req.status === 'APPROVED' && (
            <label className="field">
              <span className="field-label">{t('req.field.supplier')}</span>
              <select className="input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
                <option value="">-</option>
                {(suppliers.data || []).map((s) => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
              </select>
            </label>
          )}
          {req.status === 'CONVERTED' && req.convertedPoId && (
            <div className="alert info">{t('req.detail.converted')}</div>
          )}
          {req.decisionNote && <div className="alert danger">{req.decisionNote}</div>}
          {error && <div className="alert danger">{error}</div>}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('req.field.product')}</th>
                  <th className="num">{t('req.field.quantity')}</th>
                  <th className="num">{t('req.field.estCost')}</th>
                </tr>
              </thead>
              <tbody>
                {(req.lines || []).map((l) => (
                  <tr key={l.id}>
                    <td>{l.productSku} · {l.productName}</td>
                    <td className="num">{l.quantity}</td>
                    <td className="num">{l.estimatedCost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
